import { useEffect } from "react";
import moment from "moment";
import momentTimezone from 'moment-timezone';
import { MONTH_NAMES } from "Instrument/subfeatures/Glance/GlanceConstants";
import {ALPHANUMERIC_REGEX} from "Common/constants/regexConstants"

const formatBytes = (bytes) => {

    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(0)) + ' ' + sizes[i];
}
// method added for only allowed feature checks
const isEmptyCheck = (data) =>{
        if(data == null || data == undefined || data == ""){
            return false;
        }
        return true;
}

const isFeatureEnabled = (featureObj,featureName) => {
    if(featureObj == null || featureObj == undefined || featureObj.length<=0)
        return false;

    if(featureObj.includes(featureName.toLowerCase())){
        return true;
    }
    return false;
}

const objectPropInArray = (list,prop,val)=> {
    if (list.length > 0 ) {
      for (var i in list) {
         if (list[i][prop] === val) {
          return true;
        }
      }
    }
    return false;  
}


const useOutsideClick = (ref, callback) => {
  const handleClick = e => {
    if (ref.current && !ref.current.contains(e.target)) {
      callback();
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  });
};

const convertUtcStringToLocalString = (utcDateString, outputDateFormat, inputDateFormat) => {
  return momentTimezone
    .utc(utcDateString, inputDateFormat)
    .local()
    .format(outputDateFormat);
};

/**
 * method used to sort the input qc formatted data by field and order (direction)
 * @param {Object[]} data is the formatted qc display data
 * @param {number} order is the order in which it is to be formatted
 * @param {string} field is the field by which each data item is to be sorted
 * @returns {Object[]}
 */
const sortByFieldDirection = (data, order, field) => {
  const sortedData = [...data];
  sortedData.sort((a, b) => {
    if (a[field] === b[field]) return order === -1 ? -1 : 1;
   
    if(a[field] === null || a[field] === undefined) return order === -1 ? -1 : 1;

    if(b[field] === null || b[field] === undefined) return order === -1 ? 1 : -1;

    let cmpVal = 1;
    if(isNaN(a[field]))
      cmpVal = a[field]?.toLowerCase() < b[field]?.toLowerCase() ? -1 : 1;  
    else 
      cmpVal = a[field] < b[field] ? -1 : 1;         
    
    return order === -1 ? cmpVal : -cmpVal;
    
  });
  return sortedData;
};

const sortDateByField = (data, order, field) => {
  const sortedData = [...data];
  sortedData.sort(function(a,b){
    //For desending order
    if(order === -1) {
      return new Date(b[field]) - new Date(a[field]);
    }else return new Date(a[field]) - new Date(b[field]);
  });
  return sortedData;
};

const sortDateByFieldDirection = (data, order, field, format) => {
  const sortedData = [...data];
  sortedData.sort((a, b) => {
    if (a[field] === b[field]) return order === -1 ? -1 : 1;

    const cmpVal = moment(a[field],format) < moment(b[field],format) ? -1 : 1;
       
    return order === -1 ? cmpVal : -cmpVal;
  });
  return sortedData;
};


const removeObjInArrayWithSpecificProperty = (list,prop,val)=> {
  
  list = list.filter(x => x[prop] !== val)
   return list;  
}

/**
 * method used to get the list of unique values for a given key from a list of objects 
 * @param {Object[]} list is the flist of objects
 * @param {string} key is the key whose values need to filtered out
 * @returns {Object[]}
 */
const getUniqueValFromListByKey = (list, key) => {
  return Array.from(
    new Set(
      list.map((x) => x[key]).filter((x) => x)
    )
  );
}

const transformIntoOptions = (list) => {
  return list.map((item) => ({
    label: item,
    value: item,
  }));
}

// Function to return the dates range for the given number of days in UTC and Calendar format
const getDatesRange = (days) => {

  const currentMoment = moment();

  let endOfTheCurrentDayUTC = moment(currentMoment).utc().endOf('day').format('YYYY-MM-DD HH:mm:ss');
  let fromDateUTC = moment(currentMoment).utc().subtract(days-1, 'day').startOf('day').format("YYYY-MM-DD HH:mm:ss");
  
  return {
    fromDateUTC,
    toDateUTC: endOfTheCurrentDayUTC,
    fromDateCalendar : moment(currentMoment).subtract(days-1, 'day').format("DD/MM/YYYY"),
    toDateCalendar : moment(currentMoment).format("DD/MM/YYYY")
  }

}

const convertToArrayOfStrings = (arrayOfObjects) => {
  if (!arrayOfObjects || !Array.isArray(arrayOfObjects)) {
      return [];
  }
  return arrayOfObjects.map(item => item && item.value ? item.value : '');
};

const checkNullOrStringNull = (value) => {
  return (value == null || value === "null") ? "" : value;
}

/**
 * Prevents decimal point, minus sign, and scientific-notation characters
 * from being typed into a number input. Attach to the onKeyDown event.
 */
const preventDecimalInput = (e) => {
  if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
    e.preventDefault();
  }
};

/**
 * Extracts serializable properties from a jqXHR / error object.
 * Use this in Redux error action creators to avoid dispatching
 * non-serializable values as the payload.
 * Supports two error shapes:
 *   - Legacy jqXHR : { status: number, statusText, responseText }
 *   - API error    : { status: "403 FORBIDDEN", message, debugMessage, subErrors, timestamp }
 *
 * @param {object} err - The error / jqXHR object
 * @returns {object}
 */
const serializeError = (err) => {
  if (typeof err === "string") return err;
  return ({
  // legacy jqXHR fields
  status: err?.status,
  statusText: err?.statusText,
  responseText: err?.responseText,
  // API error fields after cybellum changes
  message: err?.message,
  debugMessage: err?.debugMessage,
  subErrors: err?.subErrors,
  timestamp: err?.timestamp,
  });
};

const formatDateToLocale = (val) => {
  if (!val) return "--";
  return new Date(val).toLocaleString();
};

const formatISODateTime = (isoStr, format = "dd Mon yyyy") => {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return isoStr;
  const day = String(d.getDate()).padStart(2, "0");
  const mon = MONTH_NAMES[d.getMonth()];
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  if (format === "Mon dd, yyyy") {
    return `${mon} ${day}, ${year} ${hh}:${mm}:${ss}`;
  }
  return `${day} ${mon} ${year} ${hh}:${mm}:${ss}`;
};

const validateTextField = (value) => {
    if (!value.trim()) return 'This field is required.';
    if (!ALPHANUMERIC_REGEX.test(value)) return 'Only alphanumeric characters, spaces, hyphens, and underscores are allowed.';
    return '';
};


export {
    formatBytes,
    isEmptyCheck,
    isFeatureEnabled,
    objectPropInArray,
    useOutsideClick,
    convertUtcStringToLocalString,
    sortByFieldDirection,
    sortDateByFieldDirection,
    removeObjInArrayWithSpecificProperty,
    getUniqueValFromListByKey,
    transformIntoOptions,
    getDatesRange,
    sortDateByField,
    convertToArrayOfStrings,
    checkNullOrStringNull,
    preventDecimalInput,
    serializeError,
    formatDateToLocale,
    formatISODateTime,
    validateTextField
}